import { useState } from 'react';
import {
  NumberInput as MantineNumberInput,
  type NumberInputProps as MantineNumberInputProps,
} from '@mantine/core';
import type { CoreComponentProps } from '../../types';
import { useShouldRender } from '../../../hooks/useShouldRender';

export type NumberInputProps = CoreComponentProps<MantineNumberInputProps, 'input'>;

function isCleared(value: string | number | undefined | null): boolean {
  return value === '' || value === undefined || value === null;
}

/**
 * VIPSo NumberInput — while focused, clearing the field stays visually empty even if
 * parents coerce empty → `0` (`Number(v) || 0` / `typeof v === 'number' ? v : 0`).
 *
 * That prevents the snap-back-to-zero and the next digit appending (`5` → `05`).
 * On blur, empty is committed as `''` so optional fields can stay blank; numeric
 * call sites still normalize with `|| 0`.
 */
export function NumberInput({
  testId,
  view = 'all',
  value,
  defaultValue,
  onChange,
  onFocus,
  onBlur,
  allowLeadingZeros = false,
  clampBehavior = 'blur',
  ...rest
}: NumberInputProps) {
  const shouldRender = useShouldRender(view);
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState<string | number | undefined>(undefined);

  if (!shouldRender) return null;

  const controlled = value !== undefined;

  // Uncontrolled: leave Mantine as-is (rare in VIPSo).
  if (!controlled) {
    return (
      <MantineNumberInput
        data-testid={testId}
        allowLeadingZeros={allowLeadingZeros}
        clampBehavior={clampBehavior}
        defaultValue={defaultValue}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        {...rest}
      />
    );
  }

  const displayValue = focused && draft !== undefined ? draft : value;

  return (
    <MantineNumberInput
      data-testid={testId}
      allowLeadingZeros={allowLeadingZeros}
      clampBehavior={clampBehavior}
      {...rest}
      value={displayValue}
      onFocus={(event) => {
        setFocused(true);
        setDraft(value as string | number);
        onFocus?.(event);
      }}
      onChange={(next) => {
        setDraft(next);
        // Emit '' when cleared so blank-optional fields work; numeric parents
        // still coerce with `Number(v) || 0` while draft keeps the input empty.
        onChange?.(isCleared(next) ? '' : next);
      }}
      onBlur={(event) => {
        const raw = draft;
        setFocused(false);
        setDraft(undefined);
        if (isCleared(raw)) {
          onChange?.('');
        } else if (typeof raw === 'string' && raw.trim() !== '') {
          const n = Number(raw);
          onChange?.(Number.isFinite(n) ? n : '');
        } else if (raw !== undefined) {
          onChange?.(raw);
        }
        onBlur?.(event);
      }}
    />
  );
}
